import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Paper,
  TablePagination,
} from '@material-ui/core';
import { GetApp as DownloadIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import config from '../../styles/utils/config.scss';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(3),
  },
  columnSelector: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  generateButton: {
    backgroundColor: config.primaryColor,
    color: '#fff',
    '&:hover': {
      backgroundColor: config.primaryButtonHoverColor,
    },
  },
  exportLoader: {
    color: config.primaryColor,
  },
  tableContainer: {
    maxHeight: 600,
    overflowY: 'auto',
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
  },
  noData: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#666',
  },
  headerCell: {
    backgroundColor: '#041e42',
    color: '#fff !important',
    fontWeight: 600,
    padding: theme.spacing(2),
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  resultsInfo: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: '#e3f2fd',
    borderRadius: 4,
  },
}));

const ProjectDetailsTab = ({ 
  data, 
  reportData, 
  loading, 
  exportLoading,
  bulkReportStatus,
  onGenerate, 
  onExport, 
  filters, 
  totalCount, 
  onPageChange,
  clearBulkReportStatus,
  showSnackbar
}) => {
  const classes = useStyles();
  const [selectedColumns, setSelectedColumns] = useState({
    projectDetails: true,
    storeDetails: true,
    userDetails: true,
  });

  // Watch for bulk report success/error and trigger download automatically
  useEffect(() => {
    if (bulkReportStatus.success && bulkReportStatus.reportType === 'projectDetails') {
      // Trigger download automatically - backend provides pre-formatted Excel data
      try {
        if (reportData && reportData.type === 'formatted') {
          downloadFormattedData(reportData.content, reportData.fileName);
        }
      } catch (error) {
        // Show user-friendly error message
        if (showSnackbar) {
          showSnackbar({
            message: 'Export failed — please try again or contact support.',
            showCloseIcon: true
          });
        }
      } finally {
        // Clear the status after download to prevent re-triggering on tab switch
        if (clearBulkReportStatus) {
          clearBulkReportStatus();
        }
      }
    }
    if (bulkReportStatus.error) {
      // Clear the error status
      if (clearBulkReportStatus) {
        clearBulkReportStatus();
      }
    }
  }, [bulkReportStatus, reportData, clearBulkReportStatus, showSnackbar]);

  const handleColumnToggle = column => {
    setSelectedColumns(prev => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  // Download pre-formatted data from backend (same approach as CommentsTab)
  const downloadFormattedData = (content, fileName) => {
    // Validate inputs
    if (!content) {
      throw new Error('Download failed: No content provided');
    }
    if (!fileName) {
      throw new Error('Download failed: No filename provided');
    }

    let blob;
    
    try {
      // Check if content is base64-encoded (Excel files from backend are typically base64)
      // Base64 strings only contain A-Z, a-z, 0-9, +, /, and = characters
      const isBase64 = typeof content === 'string' && /^[A-Za-z0-9+/]+=*$/.test(content.trim());
      
      if (isBase64) {
        try {
          // Decode base64 to binary - this can throw if content is invalid
          const binaryString = atob(content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Create blob from binary data
          // Use proper MIME type for Excel files (.xls or .xlsx)
          const mimeType = fileName.endsWith('.xlsx') 
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'application/vnd.ms-excel';
          
          blob = new Blob([bytes], { type: mimeType });
        } catch (atobError) {
          throw new Error(`Invalid base64 content: ${atobError.message}`);
        }
      } else {
        // Content is already in the correct format (HTML/text)
        blob = new Blob([content], {
          type: 'application/vnd.ms-excel'
        });
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Re-throw to be caught by calling function
      throw error;
    }
  };

  const handleExport = () => {
    // Check if at least one section is selected
    const hasSelection = selectedColumns.projectDetails || 
                        selectedColumns.storeDetails || 
                        selectedColumns.userDetails;
    
    if (!hasSelection) {
      return;
    }

    // Trigger bulk API call - download will happen automatically via useEffect
    if (onExport) {
      onExport(selectedColumns);
    }
  };

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage, filters.rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    onPageChange(0, newRowsPerPage);
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Column Selector */}
      <Paper className={classes.columnSelector}>
        <Typography variant="h6" gutterBottom>
          Select Data to Display
        </Typography>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedColumns.projectDetails}
                onChange={() => handleColumnToggle('projectDetails')}
                color="primary"
              />
            }
            label="Project Details"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedColumns.storeDetails}
                onChange={() => handleColumnToggle('storeDetails')}
                color="primary"
              />
            }
            label="Store Details"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedColumns.userDetails}
                onChange={() => handleColumnToggle('userDetails')}
                color="primary"
              />
            }
            label="User Details"
          />
        </FormGroup>
        <div className={classes.buttonContainer}>
          <Button
            variant="contained"
            className={classes.generateButton}
            onClick={handleExport}
            disabled={!data || !data.length || exportLoading}
          >
            <DownloadIcon style={{ marginRight: 8 }} />
            Export to Excel
          </Button>
          {exportLoading && (
            <CircularProgress size={24} className={classes.exportLoader} />
          )}
        </div>
      </Paper>

      {/* Results Table */}
      {!data || data.length === 0 ? (
        <div className={classes.noData}>
          <Typography variant="h6">
            No data available. Please generate a report to view results.
          </Typography>
        </div>
      ) : (
        <>
          <div className={classes.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>Store # - Seq #</TableCell>
                  <TableCell className={classes.headerCell}>Name</TableCell>
                  <TableCell className={classes.headerCell}>Modified</TableCell>
                  <TableCell className={classes.headerCell}>Closing</TableCell>
                  <TableCell className={classes.headerCell}>Real Estate Sr Manager</TableCell>
                  <TableCell className={classes.headerCell}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map(row => (
                  <TableRow key={row.id} hover className={classes.tableRow}>
                    <TableCell>{row.storeNumber} - {row.projectNumber}</TableCell>
                    <TableCell>{row.projectName}</TableCell>
                    <TableCell>{row.modifiedDate}</TableCell>
                    <TableCell>{row.projectedClosingDate || ''}</TableCell>
                    <TableCell>{row.realEstateManager || ''}</TableCell>
                    <TableCell>{row.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={filters.rowsPerPage}
            page={filters.page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </>
      )}
    </div>
  );
};

ProjectDetailsTab.propTypes = {
  data: PropTypes.array,
  reportData: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  exportLoading: PropTypes.bool,
  bulkReportStatus: PropTypes.object,
  onExport: PropTypes.func,
  filters: PropTypes.object.isRequired,
  totalCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  clearBulkReportStatus: PropTypes.func,
  showSnackbar: PropTypes.func,
};

ProjectDetailsTab.defaultProps = {
  data: [],
  reportData: [],
  exportLoading: false,
  bulkReportStatus: {},
  onExport: null,
  clearBulkReportStatus: null,
  showSnackbar: null,
};

export default ProjectDetailsTab;
